import React from 'react';
import { useTranslation } from 'react-i18next';
import type { WishlistItem } from '../../services/wishlistService';

interface WishlistDetailModalProps {
    item: WishlistItem;
    hasVoted: boolean;
    onVote: (itemId: string) => void;
    onClose: () => void;
    currentLanguage: string;
}

const WishlistDetailModal: React.FC<WishlistDetailModalProps> = ({
    item,
    hasVoted,
    onVote,
    onClose,
    currentLanguage,
}) => {
    const { t } = useTranslation(['wishlist', 'common']);

    const topicLabel = item.node
        ? (item.node.labels[currentLanguage] || item.node.labels['en'] || item.node.code)
        : item.customTopic || '';

    const statusColors: Record<string, string> = {
        open: '#22c55e',
        in_progress: '#f59e0b',
        fulfilled: '#667eea',
        declined: '#94a3b8',
    };

    const handleVote = () => {
        onVote(item.id);
    };

    return (
        <div className="wishlist-modal-overlay" onClick={onClose}>
            <div className="wishlist-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wishlist-modal__header">
                    <h2>{topicLabel}</h2>
                    <button className="wishlist-modal__close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M5 5L15 15M15 5L5 15" />
                        </svg>
                    </button>
                </div>

                <div className="wishlist-detail__meta">
                    <span
                        className="wishlist-card__status"
                        style={{ backgroundColor: statusColors[item.status] || '#94a3b8' }}
                    >
                        {t(`wishlist:status_${item.status}`)}
                    </span>
                    {item.node && (
                        <span className="wishlist-card__tag">{item.node.code}</span>
                    )}
                    <span className="wishlist-detail__author">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="7" cy="5" r="2.5" />
                            <path d="M2.5 12.5C2.5 10 4.5 8.5 7 8.5C9.5 8.5 11.5 10 11.5 12.5" />
                        </svg>
                        {item.user?.fullName || item.user?.username || ''}
                    </span>
                </div>

                {item.description && (
                    <div className="wishlist-detail__description">
                        <p>{item.description}</p>
                    </div>
                )}

                <div className="wishlist-detail__vote-section">
                    <div className="wishlist-detail__vote-count">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 3.5L12.5 8.5H17.5L13.75 11.75L15 17L10 13.75L5 17L6.25 11.75L2.5 8.5H7.5L10 3.5Z" />
                        </svg>
                        <span>{t('wishlist:voteCount', { count: item.voteCount })}</span>
                    </div>

                    <button
                        className={`wishlist-detail__vote-btn ${hasVoted ? 'wishlist-detail__vote-btn--voted' : ''}`}
                        onClick={handleVote}
                    >
                        {hasVoted ? (
                            <>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="#667eea" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 3L11.25 7.5H15.75L12.375 10.5L13.5 15.25L9 12.375L4.5 15.25L5.625 10.5L2.25 7.5H6.75L9 3Z" />
                                </svg>
                                {t('wishlist:removeVote')}
                            </>
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 3L11.25 7.5H15.75L12.375 10.5L13.5 15.25L9 12.375L4.5 15.25L5.625 10.5L2.25 7.5H6.75L9 3Z" />
                                </svg>
                                {t('wishlist:vote')}
                            </>
                        )}
                    </button>
                </div>

                {item.status === 'fulfilled' && item.fulfilledCourse && (
                    <div className="wishlist-detail__fulfilled">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 8L7 11L12 5" />
                        </svg>
                        <span>{t('wishlist:fulfilledBy')}</span>
                        <a href={`/courses/${item.fulfilledCourse.id}`}>{item.fulfilledCourse.title}</a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WishlistDetailModal;
